import { z } from 'zod';
import type { CredentialsSummary } from './credentials.js';
import type { StaffRef } from './forms.js';

export const EMAIL_STATUSES = ['scheduled', 'blocked', 'sending', 'sent', 'failed'] as const;
export type EmailStatus = (typeof EMAIL_STATUSES)[number];

export interface EmailMessage {
  id: string;
  runId: string;
  kind: 'credentials';
  status: EmailStatus;
  sendAfter: string | null;
  blockedAt: string | null;
  blockedBy: StaffRef | null;
  toEmail: string | null;
  subject: string | null;
  providerMessageId: string | null;
  attempts: number;
  lastError: string | null;
  sentAt: string | null;
}

export interface EmailPolicy {
  blocked: boolean;
  delayDays: number | null;
  /** Held for a person: never sent automatically, only by Send now once ready. */
  halted: boolean;
}

/** Everything that must be true before the email leaves; unmet ones are listed. */
export interface EmailGate {
  key:
    | 'review'
    | 'run'
    | 'blocked'
    | 'policy'
    | 'halt'
    | 'schedule'
    | 'credentials'
    | 'recipient'
    | 'canvas';
  ok: boolean;
  label: string;
}

export interface RunEmail {
  policy: EmailPolicy;
  message: EmailMessage | null;
  credentials: CredentialsSummary | null;
  gates: EmailGate[];
  /** The email as it will be sent, password masked. */
  preview: { to: string; subject: string; text: string } | null;
  /** True while Atlas still sends its own magic-link email (Atlas change A2 pending). */
  atlasAlsoEmails: boolean;
}

export interface OutboxRow extends EmailMessage {
  company: string;
}

export const EmailPolicySchema = z
  .object({
    blocked: z.boolean(),
    delayDays: z.number().int().min(0).max(3650).nullable(),
    halted: z.boolean().default(false),
  })
  // A rule about the policy as a whole, so it names no single field.
  .refine((p) => !(p.halted && p.delayDays !== null), {
    message: 'Choose either a delay or halt, not both',
  });

export const ScheduleEmailSchema = z.object({
  /** ISO instant; null clears the schedule (send as soon as the gates pass). */
  sendAfter: z.iso.datetime({ offset: true }).nullable(),
});
